import { api } from '@/lib/api';
import type {
  ContractorDashboardStats,
  ContractorJobDetail,
  ContractorJobSummary,
  ContractorProfile,
  ContractorCompany,
  ContractorMember,
  ContractorMatchItem,
  ContractorUploadedFile,
} from '@/types/contractor';

export const contractorApi = {
  getDashboard: async (): Promise<ContractorDashboardStats> => {
    const res = await api.get<ContractorDashboardStats>('/contractor/dashboard');
    return res.data;
  },

  getJobs: async (params?: {
    status?: string;
    priority?: string;
    search?: string;
  }): Promise<ContractorJobSummary[]> => {
    const res = await api.get<ContractorJobSummary[]>('/contractor/jobs', { params });
    return res.data;
  },

  getJobDetail: async (ticketId: string): Promise<ContractorJobDetail> => {
    const res = await api.get<ContractorJobDetail>(`/contractor/jobs/${ticketId}`);
    return res.data;
  },

  updateStatus: async (
    ticketId: string,
    payload: { status: string; contractor_notes?: string }
  ): Promise<ContractorJobDetail> => {
    const res = await api.patch<ContractorJobDetail>(
      `/contractor/jobs/${ticketId}/status`,
      payload
    );
    return res.data;
  },

  scheduleVisit: async (
    ticketId: string,
    payload: { scheduled_date: string; scheduled_time_window: string }
  ): Promise<ContractorJobDetail> => {
    const res = await api.post<ContractorJobDetail>(
      `/contractor/jobs/${ticketId}/schedule`,
      payload
    );
    return res.data;
  },

  submitQuote: async (
    ticketId: string,
    payload: { quote_amount: number; quote_notes?: string; quote_document_url?: string }
  ): Promise<ContractorJobDetail> => {
    const res = await api.post<ContractorJobDetail>(
      `/contractor/jobs/${ticketId}/quote`,
      payload
    );
    return res.data;
  },

  completeJob: async (
    ticketId: string,
    payload: {
      actual_cost: number;
      resolution_notes?: string;
      invoice_number?: string;
      invoice_document_url?: string;
      completion_photos?: string[];
    }
  ): Promise<ContractorJobDetail> => {
    const res = await api.post<ContractorJobDetail>(
      `/contractor/jobs/${ticketId}/complete`,
      payload
    );
    return res.data;
  },

  uploadCompletionPhoto: async (
    ticketId: string,
    file: File
  ): Promise<ContractorUploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<ContractorUploadedFile>(
      `/contractor/jobs/${ticketId}/completion-photos`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return res.data;
  },

  discardCompletionPhoto: async (ticketId: string, storedName: string): Promise<void> => {
    await api.delete(
      `/contractor/jobs/${ticketId}/completion-photos/${encodeURIComponent(storedName)}`
    );
  },

  getProfile: async (): Promise<ContractorProfile[]> => {
    const res = await api.get<ContractorProfile[]>('/contractor/profile');
    return res.data;
  },

  updateProfile: async (
    payload: Partial<ContractorProfile>
  ): Promise<ContractorProfile[]> => {
    const res = await api.patch<ContractorProfile[]>('/contractor/profile', payload);
    return res.data;
  },

  assignTechnician: async (
    ticketId: string,
    technicianUserId: string | null
  ): Promise<ContractorJobDetail> => {
    const res = await api.post<ContractorJobDetail>(
      `/contractor/jobs/${ticketId}/assign-technician`,
      { technician_user_id: technicianUserId }
    );
    return res.data;
  },

  getCompany: async (): Promise<ContractorCompany> => {
    const res = await api.get<ContractorCompany>('/contractor/company');
    return res.data;
  },

  updateCompany: async (
    payload: Partial<ContractorCompany>
  ): Promise<ContractorCompany> => {
    const res = await api.patch<ContractorCompany>('/contractor/company', payload);
    return res.data;
  },

  getTeam: async (): Promise<ContractorMember[]> => {
    const res = await api.get<ContractorMember[]>('/contractor/company/team');
    return res.data;
  },

  updateTeamMember: async (
    memberId: string,
    payload: { role?: 'admin' | 'technician'; job_title?: string | null; is_active?: boolean }
  ): Promise<ContractorMember> => {
    const res = await api.patch<ContractorMember>(`/contractor/company/team/${memberId}`, payload);
    return res.data;
  },

  inviteTeamMember: async (memberId: string): Promise<ContractorMember> => {
    const res = await api.post<ContractorMember>(`/contractor/company/team/${memberId}/invite`);
    return res.data;
  },

  inviteTeamBatch: async (memberIds: string[]): Promise<{ sent: number; failed: number; skipped: number }> => {
    const res = await api.post<{ sent: number; failed: number; skipped: number }>(
      '/contractor/company/team/invite-batch',
      { member_ids: memberIds }
    );
    return res.data;
  },

  updateCompanyStatus: async (status: 'active' | 'disabled'): Promise<ContractorCompany> => {
    const res = await api.patch<ContractorCompany>('/contractor/company/status', { status });
    return res.data;
  },

  findMatches: async (
    propertyId: string,
    params: { email?: string; tax_id?: string }
  ): Promise<ContractorMatchItem[]> => {
    const res = await api.get<ContractorMatchItem[]>(
      `/properties/${propertyId}/contractors/matches`,
      { params }
    );
    return res.data;
  },
};

